import type {
  AllOfFilter,
  AnyOfFilter,
  AssociationFilter,
  CrmFilter,
  FilterGroup,
} from '@/types/crm';

/**
 * The single definition of "this meeting is mine".
 *
 * The dashboard and the calendar answer the same question — what is on my day —
 * and used to answer it with two hand-rolled filter shapes. They drifted, and
 * the calendar's shape leaked: it scoped by `ahoy_meeting_to_integration
 * in_or_isnull [my integrations]`, whose absence arm means "has no integration
 * edge I can *see*". Since `ahoy_external_integration` is read-scoped to its
 * owner, a colleague's meeting reads as integration-less to everyone else, so
 * every org-visible meeting matched. Both surfaces now build their scoping from
 * this module so they cannot diverge again.
 *
 * Both arms are stated POSITIVELY (`in`, never `isnull`/`notin`). Nothing here
 * depends on the absence of an edge, so association-target gating
 * (`query_service._build_association_filter_q`) stays a pure narrowing filter
 * and its visible-edges-only semantics are left intact.
 *
 * The two arms are OR alternatives, and both are load-bearing:
 * - integration: a solo calendar block (focus time, lunch) has no attendees, so
 *   it carries no `ahoy_meeting_to_user` edge at all and is only reachable here.
 * - attendee: a meeting synced by a *colleague's* calendar carries their
 *   integration, not mine, and is only reachable here. Also covers meetings with
 *   no integration at all — Ahoy bookings and Recall sessions, which seed
 *   `ahoy_meeting_to_user` for their host.
 *
 * A colleague's solo block matches neither, which is the leak being closed.
 *
 * Kept in lockstep with `apps/quickfetch/views.py::_fetch_meetings`, which
 * rebuilds this same shape server-side for the dashboard's cold load. The two
 * must agree exactly or the prefetch and the live query land on different React
 * Query cache keys and refetch each other's data.
 */
export interface MineScopingOptions {
  /**
   * Also match reminders this user owns (`ahoy_reminder_to_owner`).
   *
   * The calendar merges reminders into one `entity_kind: 'event'` query, so it
   * needs this arm — a reminder is a different entity type and carries neither
   * meeting association, so the two meeting arms can never match it. (The old
   * `in_or_isnull` filter swept reminders in through its absence half, which is
   * exactly the half being removed.) The dashboard queries reminders separately
   * via buildTodayReminderFilters and leaves this off.
   */
  includeReminders?: boolean;
}

export function buildMineScopingArms(
  integrationEntityIds: string[],
  userEntityId: string | null,
  options: MineScopingOptions = {}
): CrmFilter[] {
  // Sorted so equivalent ID sets produce identical cache keys regardless of the
  // order the integrations query returned them in.
  const arms: CrmFilter[] = [
    {
      association: 'ahoy_meeting_to_integration',
      operator: 'in',
      value: [...integrationEntityIds].sort(),
    } as AssociationFilter,
  ];

  // No user entity resolved yet: fall back to the integration arm alone. That is
  // the narrower result, never the broader one.
  if (userEntityId !== null) {
    arms.push({
      association: 'ahoy_meeting_to_user',
      operator: 'in',
      value: [userEntityId],
    } as AssociationFilter);

    if (options.includeReminders === true) {
      arms.push({
        association: 'ahoy_reminder_to_owner',
        operator: 'in',
        value: [userEntityId],
      } as AssociationFilter);
    }
  }

  return arms;
}

/**
 * Cross-product each caller-supplied date group with the scoping arms above.
 *
 * Filter groups are OR'd and filters within a group are AND'd, so expressing
 * "(any of my date shapes) AND (mine)" needs one group per (date, arm) pair.
 * The dashboard has a single date shape (2 groups); the calendar has a 4-way
 * date-overlap matrix over datetime and all-day fields (16 groups), within the
 * server's `MAX_QUERY_FILTER_GROUPS` cap of 25.
 *
 * Ordering is binding: React Query cache keys embed the stringified groups, so
 * date-major/arm-minor ordering must match `_fetch_meetings` server-side.
 */
export function applyMineScoping(
  dateGroups: FilterGroup[],
  integrationEntityIds: string[],
  userEntityId: string | null,
  options: MineScopingOptions = {}
): FilterGroup[] {
  const arms = buildMineScopingArms(
    integrationEntityIds,
    userEntityId,
    options
  );
  return dateGroups.flatMap(group =>
    arms.map(arm => ({ filters: [...group.filters, arm] }))
  );
}

/**
 * Collapsed counterpart of `applyMineScoping`: instead of cross-producting
 * date groups with scoping arms into N*M flat groups, this nests both into a
 * single group of the shape `(date1 OR ... OR dateN) AND (integration OR
 * attendee OR reminder) AND advancedFilters`. It reuses `buildMineScopingArms`
 * so the scoping arms stay identical between the flat and nested shapes, and
 * — per the #13192 invariant documented above — stay stated POSITIVELY
 * (`in`, never folded into an all_of or an absence operator).
 *
 * The calendar (`useCalendarData`) uses this nested shape unconditionally;
 * the flat `applyMineScoping` remains for callers that build cross-product
 * groups (the dashboard query and its server prefetch, which must stay
 * shape-identical to each other for React Query cache-key parity).
 */
export function applyMineScopingNested(
  dateGroups: FilterGroup[],
  integrationEntityIds: string[],
  userEntityId: string | null,
  advancedFilters: CrmFilter[],
  options: MineScopingOptions = {}
): FilterGroup[] {
  const arms = buildMineScopingArms(
    integrationEntityIds,
    userEntityId,
    options
  );
  const dateNode: CrmFilter[] =
    dateGroups.length === 1
      ? dateGroups[0].filters
      : [
          {
            any_of: dateGroups.map(
              group => ({ all_of: group.filters }) as AllOfFilter
            ),
          } as AnyOfFilter,
        ];
  const scopingNode = { any_of: arms } as AnyOfFilter;
  return [{ filters: [...dateNode, scopingNode, ...advancedFilters] }];
}
